
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import maplibregl from "maplibre-gl";
import {
  FiAlertTriangle,
  FiCalendar,
  FiClock,
  FiExternalLink,
  FiMapPin,
  FiNavigation,
  FiTruck,
  FiChevronRight,
  FiX,
} from "react-icons/fi";

import axios from "../../axiosConfig";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../styles/CreateLoadRoutePreview.css";

const MAP_STYLES = {
  dark: "https://tiles.openfreemap.org/styles/dark",
  light: "https://tiles.openfreemap.org/styles/liberty",
};

const getRoutePreviewThemeNode = (element) => {
  return (
    element?.closest("[data-theme]") ||
    document
      .querySelector(".create-load-atlas-layout")
      ?.closest("[data-theme]") ||
    document.body?.closest("[data-theme]") ||
    document.documentElement
  );
};

const getRoutePreviewMapStyle = (element) => {
  const theme = getRoutePreviewThemeNode(element)
    ?.getAttribute("data-theme");

  return theme === "light"
    ? MAP_STYLES.light
    : MAP_STYLES.dark;
};

// Independent preview cache. No Transport Session state is modified.
const routeCache = new Map();

const readCoordinates = (raw) => {
  const parts = String(raw ?? "")
    .trim()
    .split(/[,;\s]+/)
    .filter(Boolean);

  if (parts.length !== 2) return null;

  const [lat, lng] = parts.map(Number);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    Math.abs(lat) > 90 ||
    Math.abs(lng) > 180 ||
    (lat === 0 && lng === 0)
  ) {
    return null;
  }

  return { lat, lng };
};

const getCountryFlagUrl = (country) => {
  const code = String(country || "").trim().toLowerCase();

  if (!/^[a-z]{2}$/.test(code)) return "";

  return `https://flagcdn.com/w40/${code}.png`;
};

const formatDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
    return "—";
  }

  const parsed = new Date(`${value}T12:00:00`);

  if (Number.isNaN(parsed.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
  }).format(parsed);
};

const durationLabel = (seconds) => {
  if (
    seconds == null ||
    !Number.isFinite(Number(seconds))
  ) {
    return "—";
  }

  const minutes = Math.round(Number(seconds) / 60);

  return `${Math.floor(minutes / 60)}h ${String(
    minutes % 60
  ).padStart(2, "0")}m`;
};

const formatStopStamp = (stamp) => {
  if (!stamp?.local) return "—";

  const [date, hour] = String(stamp.local).split("T");

  return `${formatDate(date)} · ${
    hour?.slice(0, 5) || "—"
  }`;
};

const routeFromResponse = (data) => {
  const item = data?.routes?.[0];

  if (!item) return null;

  const raw = item.geometry;

  const geometry =
    raw?.type === "Feature"
      ? raw
      : raw?.type === "LineString"
      ? {
          type: "Feature",
          geometry: raw,
          properties: {},
        }
      : null;

  if (
    geometry?.geometry?.type !== "LineString" ||
    !Array.isArray(geometry.geometry.coordinates) ||
    geometry.geometry.coordinates.length < 2
  ) {
    return null;
  }

  const optionalNumber = (value) =>
    value == null ||
    value === "" ||
    !Number.isFinite(Number(value))
      ? null
      : Number(value);

  return {
    geometry,
    distanceKm: optionalNumber(
      item.distanceKm ?? item.distance_km
    ),
    durationSeconds: optionalNumber(
      item.durationSeconds ?? item.duration_seconds
    ),
  };
};

function RoutePreviewMap({ stops, route }) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const fitKeyRef = useRef("");

  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [styleRevision, setStyleRevision] = useState(0);

  const styleReadyRef = useRef(false);

  useEffect(() => {
    if (!hostRef.current) return undefined;

    let map;
    let observer;
    let themeObserver;
    let loaded = false;

    let activeStyle = getRoutePreviewMapStyle(
      hostRef.current
    );

    styleReadyRef.current = false;

    try {
      map = new maplibregl.Map({
        container: hostRef.current,
        style: activeStyle,
        center: [-5.2, 46.3],
        zoom: 3.1,
        attributionControl: false,
      });

      mapRef.current = map;

      map.addControl(
        new maplibregl.NavigationControl({
          showCompass: false,
        }),
        "bottom-right"
      );

      map.on("style.load", () => {
        loaded = true;
        styleReadyRef.current = true;

        setFailed(false);
        setReady(true);
        setStyleRevision((previous) => previous + 1);
      });

      map.on("error", (event) => {
        if (!loaded) {
          console.warn(
            "[CreateLoadRoutePreview] map error",
            event?.error
          );

          setFailed(true);
        }
      });

      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(() => {
          map.resize();
        });

        observer.observe(hostRef.current);
      }

      if (typeof MutationObserver !== "undefined") {
        themeObserver = new MutationObserver(() => {
          const nextStyle = getRoutePreviewMapStyle(
            hostRef.current
          );

          if (nextStyle === activeStyle) {
            return;
          }

          activeStyle = nextStyle;
          styleReadyRef.current = false;
          setReady(false);

          map.setStyle(nextStyle);
        });

        const pageRoot = document.querySelector(
          ".create-load-atlas-layout"
        );

        const themeNodes = [
          document.documentElement,
          document.body,
          pageRoot,
          pageRoot?.closest("[data-theme]"),
          hostRef.current.closest("[data-theme]"),
        ];

        const observedNodes = new Set();

        themeNodes.forEach((node) => {
          if (!node || observedNodes.has(node)) {
            return;
          }

          observedNodes.add(node);

          themeObserver.observe(node, {
            attributes: true,
            attributeFilter: ["data-theme", "class"],
          });
        });
      }
    } catch (error) {
      console.warn(
        "[CreateLoadRoutePreview] failed to initialize map",
        error
      );

      setFailed(true);
    }

    return () => {
      observer?.disconnect();
      themeObserver?.disconnect();

      styleReadyRef.current = false;

      markersRef.current.forEach((marker) => {
        marker.remove();
      });

      markersRef.current = [];
      mapRef.current = null;

      map?.remove();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;

    if (!ready || !map || !styleReadyRef.current) return;

    markersRef.current.forEach((marker) => {
      marker.remove();
    });

    markersRef.current = [];

    const sourceId = "cl-route-preview-line";
    const casingId = "cl-route-preview-casing";
    const lineId = "cl-route-preview-stroke";

    [lineId, casingId].forEach((id) => {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    });

    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    if (route?.geometry) {
      map.addSource(sourceId, {
        type: "geojson",
        data: route.geometry,
      });

      map.addLayer({
        id: casingId,
        type: "line",
        source: sourceId,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#041629",
          "line-width": 7,
          "line-opacity": 0.95,
        },
      });

      map.addLayer({
        id: lineId,
        type: "line",
        source: sourceId,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#238BFF",
          "line-width": 3.5,
        },
      });
    }

    const located = stops.filter(
      (stop) => stop.coords
    );

    located.forEach((stop) => {
      const el = document.createElement("div");

      const last =
        stop.kind === "delivery" &&
        stop.order === stops.length;

      el.className = `cl-rp-pin ${
        stop.kind === "pickup"
          ? "is-pickup"
          : last
          ? "is-final"
          : "is-delivery"
      }`;

      el.textContent = String(stop.order);
      el.title = stop.name;

      const marker = new maplibregl.Marker({
        element: el,
        anchor: "center",
      })
        .setLngLat([
          stop.coords.lng,
          stop.coords.lat,
        ])
        .setPopup(
          new maplibregl.Popup({
            offset: 16,
          }).setText(stop.name)
        )
        .addTo(map);

      markersRef.current.push(marker);
    });

    const fitKey =
      located
        .map(
          (stop) =>
            `${stop.coords.lat}:${stop.coords.lng}`
        )
        .join("|") +
      (route?.geometry ? ":r" : "");

    if (!fitKey || fitKeyRef.current === fitKey) {
      return;
    }

    fitKeyRef.current = fitKey;

    if (located.length === 1) {
      map.flyTo({
        center: [
          located[0].coords.lng,
          located[0].coords.lat,
        ],
        zoom: 9,
        duration: 450,
      });
    } else {
      const bounds = new maplibregl.LngLatBounds();

      located.forEach((stop) => {
        bounds.extend([
          stop.coords.lng,
          stop.coords.lat,
        ]);
      });

      route?.geometry?.geometry?.coordinates?.forEach(
        (point) => {
          if (
            Array.isArray(point) &&
            Number.isFinite(point[0]) &&
            Number.isFinite(point[1])
          ) {
            bounds.extend(point);
          }
        }
      );

      map.fitBounds(bounds, {
        padding: 38,
        maxZoom: 10,
        duration: 450,
      });
    }
  }, [ready, stops, route, styleRevision]);

  return (
    <div className="cl-rp-map-wrap">
      <div
        ref={hostRef}
        className="cl-rp-map-canvas"
      />

      {failed && (
        <div className="cl-rp-map-message">
          Map tiles unavailable.
        </div>
      )}
    </div>
  );
}

const CreateLoadAtlasPanel = ({
  pickups = [],
  deliveries = [],
  importJob,
  aiJourney,
  onEditStop,
}) => {
  const [expandedMap, setExpandedMap] = useState(false);

  const [routeState, setRouteState] = useState({
    route: null,
    loading: false,
    error: "",
  });

  const stops = useMemo(
    () =>
      [
        ...pickups.map((place, index) => ({
          kind: "pickup",
          index,
          place,
        })),
        ...deliveries.map((place, index) => ({
          kind: "delivery",
          index,
          place,
        })),
      ].map((item, index) => ({
        ...item,
        key:
          item.place._uiId ||
          `${item.kind}-${item.index}`,
        order: index + 1,
        name:
          item.place.location ||
          `${
            item.kind === "pickup"
              ? "Pickup"
              : "Delivery"
          } ${item.index + 1}`,
        coords: readCoordinates(
          item.place.coordinates
        ),
      })),
    [pickups, deliveries]
  );

  const locatedCount = stops.filter(
    (item) => item.coords
  ).length;

  const complete =
    stops.length >= 2 &&
    locatedCount === stops.length;

  // Coordinate/order signature: editing unrelated fields
  // does not trigger another Valhalla request.
  const routeKey = complete
    ? stops
        .map(
          (item) =>
            `${item.kind}:${item.coords.lat}:${item.coords.lng}`
        )
        .join("|")
    : "";

  const scheduleKey = complete
    ? JSON.stringify(
        stops.map((item) => ({
          kind: item.kind.toUpperCase(),
          date: item.place.date || "",
          hour: item.place.hour || "",

          scheduleType: String(
            item.place.scheduleType ||
              (item.place.hourEnd ? "WINDOW" : "FIXED")
          ).toUpperCase(),

          hourEnd: item.place.hourEnd || null,

          country: item.place.country || "",

          timeZone:
            item.place.timeZone ||
            item.place.timezone ||
            null,

          operationMinutes:
            item.place.operationMinutes === "" ||
            item.place.operationMinutes == null
              ? null
              : item.place.operationMinutes,
        }))
      )
    : "";

  const requestKey = routeKey
    ? `${routeKey}|schedule:${scheduleKey}`
    : "";

  useEffect(() => {
    if (!requestKey) {
      setRouteState({
        route: null,
        loading: false,
        error: "",
      });

      return undefined;
    }

    if (routeCache.has(requestKey)) {
      setRouteState({
        route: routeCache.get(requestKey),
        loading: false,
        error: "",
      });

      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;

    setRouteState({
      route: null,
      loading: true,
      error: "",
    });

    const timer = window.setTimeout(async () => {
      const parts = routeKey.split("|");

      const locations = parts.map(
        (part, index) => {
          const [, lat, lng] = part.split(":");

          return {
            id: `create-load-preview-${index}`,
            type:
              index === 0
                ? "ORIGIN"
                : index === parts.length - 1
                ? "DESTINATION"
                : "VIA",
            sequence: index + 1,
            lat: Number(lat),
            lon: Number(lng),
          };
        }
      );

      try {
        const { data } = await axios.post(
          "/maps/route",
          {
            costing: "truck",
            routePreview: true,
            alternatives: 0,
            avoidTolls: false,
            includeTollEstimate: false,
            tollVehicleClass: 4,
            locations,
            routePreviewStops: JSON.parse(scheduleKey),
          },
          {
            signal: controller.signal,
          }
        );

        if (cancelled) return;

        console.info("[RoutePreview DIAGNOSTIC] HTTP response", {
          ok: data?.ok,
          provider: data?.provider,
          routeCount: data?.routeCount,
          routesReceived: Array.isArray(data?.routes)
            ? data.routes.length
            : "NOT_ARRAY",
          geometryType: data?.routes?.[0]?.geometry?.type,
          geometryPoints:
            data?.routes?.[0]?.geometry?.geometry
              ?.coordinates?.length ?? null,
          operationalStatus: data?.operationalSchedule?.status,
        });

        const normalizedRoute = routeFromResponse(data);

        console.info("[RoutePreview DIAGNOSTIC] Normalization", {
          valid: Boolean(normalizedRoute),
          distanceKm: normalizedRoute?.distanceKm,
          durationSeconds: normalizedRoute?.durationSeconds,
        });

        if (!normalizedRoute) {
          throw new Error(
            "No usable road route was returned."
          );
        }

        const route = {
          ...normalizedRoute,
          operationalEstimate:
            data?.operationalEstimate ?? null,
          operationalSchedule:
            data?.operationalSchedule ?? null,
        };

        routeCache.set(requestKey, route);

        if (routeCache.size > 40) {
          routeCache.delete(
            routeCache.keys().next().value
          );
        }

        setRouteState({
          route,
          loading: false,
          error: "",
        });
      } catch (error) {
        if (!cancelled) {
          console.error("[RoutePreview DIAGNOSTIC] FAILED", {
            message: error?.message,
            code: error?.code ?? null,
            httpStatus: error?.response?.status ?? null,
            responseData: error?.response?.data ?? null,
            xhrStatus: error?.request?.status ?? null,
            xhrReadyState: error?.request?.readyState ?? null,
            requestKey,
            aborted: controller.signal.aborted,
          });

          setRouteState({
            route: null,
            loading: false,
            error: "Road route unavailable.",
          });
        }
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [routeKey, requestKey, scheduleKey]);

  useEffect(() => {
    if (!expandedMap) return undefined;

    const close = (event) => {
      if (event.key === "Escape") {
        setExpandedMap(false);
      }
    };

    window.addEventListener("keydown", close);

    return () => {
      window.removeEventListener("keydown", close);
    };
  }, [expandedMap]);

  const lastDelivery =
    deliveries[deliveries.length - 1];

  const lastIsWindow =
    String(
      lastDelivery?.scheduleType ||
        (lastDelivery?.hourEnd ? "WINDOW" : "FIXED")
    ).toUpperCase() === "WINDOW";

  const lastScheduled = lastDelivery?.date
    ? `${formatDate(lastDelivery.date)}${
        lastDelivery.hour
          ? ` · ${lastDelivery.hour}${
              lastIsWindow && lastDelivery.hourEnd
                ? `–${lastDelivery.hourEnd}`
                : ""
            }`
          : ""
      }`
    : "—";

  const distance =
    routeState.route?.distanceKm;

  const distanceText =
    distance == null
      ? "—"
      : `${new Intl.NumberFormat("en-GB", {
          maximumFractionDigits: 0,
        }).format(distance)} km`;

  const working =
    importJob &&
    !["COMPLETED", "FAILED"].includes(
      String(importJob.status || "").toUpperCase()
    );

  return (
    <>
      <aside
        className="cl-atlas cl-route-preview"
        aria-label="Route preview"
      >
        <div className="cl-rp-heading">
          <strong>Route preview</strong>

          <button
            type="button"
            onClick={() => setExpandedMap(true)}
            disabled={!locatedCount}
          >
            View on map
            <FiExternalLink aria-hidden="true" />
          </button>
        </div>

        {aiJourney && (
          <div className="cl-rp-ai-journey">
            {aiJourney}
          </div>
        )}

        <div className="cl-rp-map-frame">
          <RoutePreviewMap
            stops={stops}
            route={routeState.route}
          />

          {routeState.loading && (
            <div className="cl-rp-map-tag">
              Calculating route…
            </div>
          )}

          {!complete && (
            <div className="cl-rp-map-tag">
              {locatedCount}/{stops.length} locations mapped
            </div>
          )}
        </div>

        <div className="cl-rp-stops-heading">
          <strong>{stops.length} stops</strong>

          <span>
            <FiTruck aria-hidden="true" />
            {distanceText}
          </span>
        </div>

        <div className="cl-rp-stop-list">
          {stops.map((stop) => (
            <button
              type="button"
              key={stop.key}
              className="cl-rp-stop"
              onClick={() =>
                onEditStop?.(
                  stop.kind,
                  stop.index
                )
              }
              title={`Edit ${stop.name}`}
            >
              <span
                className={`cl-rp-number ${
                  stop.kind === "pickup"
                    ? "is-pickup"
                    : stop.order === stops.length
                    ? "is-final"
                    : "is-delivery"
                }`}
              >
                {stop.order}
              </span>

              <span className="cl-rp-stop-label">
                <strong title={stop.name}>
                  {stop.name}
                </strong>

                <small>
                  {getCountryFlagUrl(stop.place.country) && (
                    <img
                      className="cl-rp-stop-country-flag"
                      src={getCountryFlagUrl(stop.place.country)}
                      alt=""
                      aria-hidden="true"
                      width="18"
                      height="13"
                      loading="lazy"
                    />
                  )}

                  <span className="cl-rp-stop-location">
                    {[
                      stop.place.city,
                      stop.place.country,
                    ]
                      .filter(Boolean)
                      .join(", ") ||
                      "Location pending"}
                  </span>
                </small>
              </span>

              <span className="cl-rp-stop-date">
                <span>{formatDate(stop.place.date)}</span>

                {stop.place.hour && (
                  <small>{stop.place.hour}</small>
                )}
              </span>

              <span
                className="cl-rp-stop-action"
                aria-hidden="true"
              >
                <FiChevronRight />
              </span>
            </button>
          ))}
        </div>

        {routeState.route?.operationalSchedule?.status === "OK" && (
          <section className="cl-rp-schedule">
            <strong className="cl-rp-schedule-title">
              Stop-by-stop forecast
            </strong>

            {routeState.route.operationalSchedule.stops.map(
              (planned, index) => (
                <div
                  className="cl-rp-schedule-stop"
                  key={stops[index]?.key || index}
                >
                  <div className="cl-rp-schedule-stop-title">
                    <span>{index + 1}</span>
                    <strong>
                      {stops[index]?.name ||
                        `Stop ${index + 1}`}
                    </strong>
                  </div>

                  <div className="cl-rp-schedule-row">
                    <span>
                      {index === 0
                        ? "Scheduled start"
                        : "Estimated arrival"}
                    </span>
                    <b>{formatStopStamp(planned.arrival)}</b>
                  </div>

                  {planned.waitingSeconds > 0 && (
                    <div className="cl-rp-schedule-row">
                      <span>
                        {planned.scheduleType === "WINDOW"
                          ? "Waiting for window to open"
                          : "Waiting for appointment"}
                      </span>
                      <b>
                        {durationLabel(
                          planned.waitingSeconds
                        )}
                      </b>
                    </div>
                  )}

                  <div className="cl-rp-schedule-row">
                    <span>
                      {planned.kind === "PICKUP"
                        ? "Estimated loading"
                        : "Estimated unloading"}
                    </span>
                    <b>
                      {durationLabel(
                        planned.operationSeconds
                      )}
                    </b>
                  </div>

                  <div className="cl-rp-schedule-row">
                    <span>
                      {planned.kind === "PICKUP"
                        ? "Estimated departure"
                        : "Estimated completion"}
                    </span>
                    <b>
                      {formatStopStamp(
                        planned.departure
                      )}
                    </b>
                  </div>

                  {planned.delaySeconds > 0 && (
                    <div className="cl-rp-schedule-delay">
                      <FiAlertTriangle />
                      <span>
                        Estimated delay ·{" "}
                        {durationLabel(
                          planned.delaySeconds
                        )}
                      </span>
                    </div>
                  )}

                  {planned.scheduleType === "WINDOW" &&
                    planned.marginSeconds > 0 && (
                      <div className="cl-rp-schedule-row">
                        <span>Time remaining in window</span>
                        <b>
                          {durationLabel(
                            planned.marginSeconds
                          )}
                        </b>
                      </div>
                    )}
                </div>
              )
            )}

            <small className="cl-rp-schedule-footnote">
              Times shown in each stop's local time zone.{" "}
              {stops.some(
                (stop) =>
                  String(
                    stop.place.scheduleType ||
                      (stop.place.hourEnd ? "WINDOW" : "FIXED")
                  ).toUpperCase() === "WINDOW"
              )
                ? "Time-window scheduling enabled."
                : "Fixed appointment scenario."}
            </small>
          </section>
        )}

        {routeState.route?.operationalSchedule &&
          routeState.route.operationalSchedule.status !== "OK" && (
            <p className="cl-rp-info">
              {routeState.route.operationalSchedule.status ===
              "START_NOT_SCHEDULED"
                ? "Enter the first pickup date and time to calculate arrival forecasts."
                : `Operational schedule pending: ${routeState.route.operationalSchedule.status}. Check stop dates, countries and operation durations.`}
            </p>
          )}

        <div className="cl-rp-metrics">
          <strong>Estimated details</strong>

          <div>
            <span>
              <FiNavigation />
              Distance
            </span>
            <b>{distanceText}</b>
          </div>

          <div>
            <span>
              <FiClock />
              Driving time
            </span>
            <b>
              {durationLabel(
                routeState.route?.durationSeconds
              )}
            </b>
          </div>

          <div>
            <span>
              <FiClock />
              Mandatory breaks
            </span>
            <b>
              {durationLabel(
                routeState.route
                  ?.operationalEstimate
                  ?.breakSeconds
              )}
            </b>
          </div>

          <div>
            <span>
              <FiClock />
              Daily rests
            </span>
            <b>
              {durationLabel(
                routeState.route
                  ?.operationalEstimate
                  ?.dailyRestSeconds
              )}
            </b>
          </div>

          {routeState.route?.operationalSchedule?.status === "OK" && (
            <>
              <div>
                <span>
                  <FiClock />
                  Loading / unloading
                </span>
                <b>
                  {durationLabel(
                    routeState.route.operationalSchedule
                      .totals.operationSeconds
                  )}
                </b>
              </div>

              <div>
                <span>
                  <FiClock />
                  Waiting
                </span>
                <b>
                  {durationLabel(
                    routeState.route.operationalSchedule
                      .totals.waitingSeconds
                  )}
                </b>
              </div>
            </>
          )}

          <div>
            <span>
              <FiNavigation />
              {routeState.route?.operationalSchedule?.status === "OK"
                ? "Total elapsed"
                : "Journey duration"}
            </span>
            <b>
              {durationLabel(
                routeState.route?.operationalSchedule?.status === "OK"
                  ? routeState.route.operationalSchedule
                      .totals.totalElapsedSeconds
                  : routeState.route?.operationalEstimate
                      ?.totalElapsedSeconds
              )}
            </b>
          </div>

          <div>
            <span>
              <FiCalendar />
              Final delivery (scheduled)
            </span>
            <b>{lastScheduled}</b>
          </div>
        </div>

        {working && (
          <p className="cl-rp-info">
            <FiMapPin />
            Location verification is still in progress.
          </p>
        )}

        {!complete && !working && (
          <p className="cl-rp-info">
            Complete all stop coordinates to calculate
            the full route.
          </p>
        )}

        {routeState.error && (
          <p className="cl-rp-info is-error">
            {routeState.error}
          </p>
        )}

        <div className="cl-rp-footnote">
          Load preview only · Transport Session unchanged
        </div>
      </aside>

      {expandedMap &&
        createPortal(
          <div
            className="cl-rp-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (
                event.target === event.currentTarget
              ) {
                setExpandedMap(false);
              }
            }}
          >
            <div
              className="cl-rp-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Full route map"
            >
              <div className="cl-rp-modal-header">
                <strong>
                  Route preview · {stops.length} stops
                </strong>

                <button
                  type="button"
                  aria-label="Close full map"
                  onClick={() =>
                    setExpandedMap(false)
                  }
                >
                  <FiX />
                </button>
              </div>

              <RoutePreviewMap
                stops={stops}
                route={routeState.route}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default CreateLoadAtlasPanel;

